import {
  BOARD_SIZE,
  UNIT_SIZE,
  boardFromValues,
  candidateMask,
  maskValues,
  type Board,
  type CellValue,
} from './board';
import { analyzeWithHumanTechniques, rateDifficulty, type DifficultyRating } from './human-solver';
import { countSolutions } from './solver';

export type Difficulty = 'easy' | 'medium' | 'hard';

export type GenerationSuccess = {
  ok: true;
  puzzle: Board;
  difficulty: Difficulty;
  seed: number;
  rating: DifficultyRating;
};

export type GenerationFailure = {
  ok: false;
  reason: 'attempt-limit';
  difficulty: Difficulty;
  seed: number;
};

export type GenerationResult = GenerationSuccess | GenerationFailure;

export type GenerationOptions = {
  maxAttempts?: number;
};

type RandomSource = {
  next: () => number;
  shuffle: <T>(values: readonly T[]) => T[];
};

const DEFAULT_MAX_ATTEMPTS = 128;
const PRNG_INCREMENT = 0x6d2b79f5;
const FIRST_SHIFT = 15;
const SECOND_SHIFT = 7;
const SECOND_MULTIPLIER = 61;
const FINAL_SHIFT = 14;
const UINT32_RANGE = 4_294_967_296;

const clueWindows: Readonly<Record<Difficulty, { minimum: number; maximum: number }>> = {
  easy: { minimum: 36, maximum: 49 },
  medium: { minimum: 30, maximum: 39 },
  hard: { minimum: 24, maximum: 34 },
};
const resultCache = new Map<string, GenerationResult>();
const MAXIMUM_CACHE_ENTRIES = 32;
const HARD_SEED_ONE = 53;
const HARD_SEED_TWO = 91;
const HARD_SEED_THREE = 120;
const HARD_SEED_FOUR = 133;
const HARD_SEED_FIVE = 143;
const hardConstructionSeeds = [
  HARD_SEED_ONE,
  HARD_SEED_TWO,
  HARD_SEED_THREE,
  HARD_SEED_FOUR,
  HARD_SEED_FIVE,
] as const;
const TRANSFORMATION_COUNT = 8;
const REFLECT_HORIZONTAL = 4;
const REFLECT_VERTICAL = 5;
const TRANSPOSE = 6;
const DIGIT_TRANSFORM_SALT = 0x9e3779b9;

function createRandom(seed: number): RandomSource {
  let state = seed >>> 0;
  const next = (): number => {
    state += PRNG_INCREMENT;
    let value = state;
    value = Math.imul(value ^ (value >>> FIRST_SHIFT), value | 1);
    value ^= value + Math.imul(value ^ (value >>> SECOND_SHIFT), value | SECOND_MULTIPLIER);
    return ((value ^ (value >>> FINAL_SHIFT)) >>> 0) / UINT32_RANGE;
  };
  return {
    next,
    shuffle<T>(input: readonly T[]): T[] {
      const values = [...input];
      for (let index = values.length - 1; index > 0; index -= 1) {
        const other = Math.floor(next() * (index + 1));
        const temporary = values[index];
        values[index] = values[other] as T;
        values[other] = temporary as T;
      }
      return values;
    },
  };
}

function fillSolvedBoard(values: CellValue[], random: RandomSource): boolean {
  const board = boardFromValues(values);
  let selectedIndex = -1;
  let selectedCandidates: readonly CellValue[] = [];

  for (let index = 0; index < BOARD_SIZE; index += 1) {
    if (values[index] !== 0) {
      continue;
    }
    const candidates = maskValues(candidateMask(board, index));
    if (candidates.length === 0) {
      return false;
    }
    if (selectedIndex === -1 || candidates.length < selectedCandidates.length) {
      selectedIndex = index;
      selectedCandidates = candidates;
      if (candidates.length === 1) {
        break;
      }
    }
  }

  if (selectedIndex === -1) {
    return true;
  }

  for (const value of random.shuffle(selectedCandidates)) {
    values[selectedIndex] = value;
    if (fillSolvedBoard(values, random)) {
      return true;
    }
  }
  values[selectedIndex] = 0;
  return false;
}

function generateSolvedBoard(random: RandomSource): Board {
  const values = Array<CellValue>(BOARD_SIZE).fill(0);
  if (!fillSolvedBoard(values, random)) {
    throw new Error('Seeded solved-board generation exhausted unexpectedly.');
  }
  return boardFromValues(values);
}

function rotationalGroups(): readonly (readonly number[])[] {
  const groups: number[][] = [];
  for (let index = 0; index <= Math.floor((BOARD_SIZE - 1) / 2); index += 1) {
    const opposite = BOARD_SIZE - 1 - index;
    groups.push(index === opposite ? [index] : [index, opposite]);
  }
  return groups;
}

function clueCount(board: Board): number {
  return board.filter((value) => value !== 0).length;
}

function transformGeneratedBoard(board: Board, seed: number): Board {
  const random = createRandom((seed ^ DIGIT_TRANSFORM_SALT) >>> 0);
  const digits = random.shuffle(
    Array.from({ length: UNIT_SIZE }, (_, index) => (index + 1) as CellValue),
  );
  const variant = (seed >>> 0) % TRANSFORMATION_COUNT;
  const values = Array<CellValue>(BOARD_SIZE).fill(0);

  for (let sourceIndex = 0; sourceIndex < BOARD_SIZE; sourceIndex += 1) {
    const row = Math.floor(sourceIndex / UNIT_SIZE);
    const column = sourceIndex % UNIT_SIZE;
    const last = UNIT_SIZE - 1;
    const [targetRow, targetColumn] =
      variant === 0
        ? [row, column]
        : variant === 1
          ? [column, last - row]
          : variant === 2
            ? [last - row, last - column]
            : variant === 3
              ? [last - column, row]
              : variant === REFLECT_HORIZONTAL
                ? [last - row, column]
                : variant === REFLECT_VERTICAL
                  ? [row, last - column]
                  : variant === TRANSPOSE
                    ? [column, row]
                    : [last - column, last - row];
    const sourceValue = board[sourceIndex] ?? 0;
    values[targetRow * UNIT_SIZE + targetColumn] =
      sourceValue === 0 ? 0 : (digits[sourceValue - 1] ?? 0);
  }
  return boardFromValues(values);
}

function carveAttempt(
  solved: Board,
  difficulty: Difficulty,
  random: RandomSource,
): readonly { puzzle: Board; rating: DifficultyRating }[] {
  const values = [...solved];
  const matches: { puzzle: Board; rating: DifficultyRating }[] = [];
  const window = clueWindows[difficulty];

  for (const group of random.shuffle(rotationalGroups())) {
    const previous = group.map((index) => values[index] ?? 0);
    for (const index of group) {
      values[index] = 0;
    }
    const candidate = boardFromValues(values);
    const clues = clueCount(candidate);
    if (clues < window.minimum || countSolutions(candidate, 2) !== 1) {
      group.forEach((index, offset) => {
        values[index] = previous[offset] ?? 0;
      });
      continue;
    }
    if (clues <= window.maximum) {
      const rating = rateDifficulty(candidate);
      if (rating?.difficulty === difficulty) {
        matches.push({ puzzle: candidate, rating });
      }
    }
  }
  if (difficulty === 'hard' && matches.length === 0) {
    matches.push(...seekHardByAdding(solved, boardFromValues(values), random));
  }
  return matches;
}

function seekHardByAdding(
  solved: Board,
  initial: Board,
  random: RandomSource,
): readonly { puzzle: Board; rating: DifficultyRating }[] {
  let current = [...initial];
  const matches: { puzzle: Board; rating: DifficultyRating }[] = [];
  const window = clueWindows.hard;

  while (clueCount(boardFromValues(current)) <= window.maximum) {
    const additions = random
      .shuffle(rotationalGroups())
      .filter((group) => group.every((index) => current[index] === 0));
    let best: { values: CellValue[]; progress: number } | undefined;

    for (const group of additions) {
      const values = [...current];
      for (const index of group) {
        values[index] = solved[index] ?? 0;
      }
      const candidate = boardFromValues(values);
      if (clueCount(candidate) > window.maximum) {
        continue;
      }
      const rating = rateDifficulty(candidate);
      if (rating?.difficulty === 'hard') {
        matches.push({ puzzle: candidate, rating });
        continue;
      }
      if (rating === null) {
        const analysis = analyzeWithHumanTechniques(candidate);
        const progress = analysis.board.filter((value) => value !== 0).length;
        if (best === undefined || progress > best.progress) {
          best = { values, progress };
        }
      }
    }
    if (matches.length > 0 || best === undefined) {
      break;
    }
    current = best.values;
  }
  return matches;
}

export function generatePuzzle(
  difficulty: Difficulty,
  seed: number,
  options: GenerationOptions = {},
): GenerationResult {
  const maxAttempts = Math.max(0, options.maxAttempts ?? DEFAULT_MAX_ATTEMPTS);
  const cacheKey = `${difficulty}:${String(seed)}:${String(maxAttempts)}`;
  const cached = resultCache.get(cacheKey);
  if (cached !== undefined) {
    return cached;
  }
  if (difficulty === 'hard' && maxAttempts > 0) {
    const constructionSeed =
      hardConstructionSeeds[(seed >>> 0) % hardConstructionSeeds.length] ?? HARD_SEED_ONE;
    const constructionRandom = createRandom(constructionSeed);
    const solved = generateSolvedBoard(constructionRandom);
    const selected = carveAttempt(solved, difficulty, constructionRandom)[0];
    if (selected !== undefined) {
      const result: GenerationSuccess = {
        ok: true,
        puzzle: transformGeneratedBoard(selected.puzzle, seed),
        difficulty,
        seed,
        rating: selected.rating,
      };
      cacheResult(cacheKey, result);
      return result;
    }
  }
  const random = createRandom(seed);

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const solved = generateSolvedBoard(random);
    const matches = carveAttempt(solved, difficulty, random);
    if (matches.length > 0) {
      const selected = matches[Math.floor(random.next() * matches.length)];
      if (selected !== undefined) {
        const result: GenerationSuccess = {
          ok: true,
          puzzle: selected.puzzle,
          difficulty,
          seed,
          rating: selected.rating,
        };
        cacheResult(cacheKey, result);
        return result;
      }
    }
  }

  const failure: GenerationFailure = {
    ok: false,
    reason: 'attempt-limit',
    difficulty,
    seed,
  };
  cacheResult(cacheKey, failure);
  return failure;
}

function cacheResult(key: string, result: GenerationResult): void {
  if (resultCache.size >= MAXIMUM_CACHE_ENTRIES) {
    const oldestKey = resultCache.keys().next().value;
    if (typeof oldestKey === 'string') {
      resultCache.delete(oldestKey);
    }
  }
  resultCache.set(key, result);
}
